import React, { useState } from "react";
import { BlogPost } from "@/types/blog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent } from "@/components/ui/card";
import { 
  Search, 
  Edit, 
  Trash2, 
  Eye, 
  List, 
  Grid, 
  Loader 
} from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { isBrowser } from "@/utils/runtime";

interface BlogListProps {
  posts: BlogPost[];
  viewMode: "list" | "grid";
  searchTerm: string;
  tabFilter: string;
  isLoading: boolean;
  isDeleting: string | null;
  onEditPost: (id: string) => void;
  onViewPost: (post: BlogPost) => void;
  onSetViewMode: (mode: "list" | "grid") => void;
  onSearchChange: (term: string) => void;
  onFilterChange: (filter: string) => void;
  onLoadPosts: () => void;
}

const BlogList: React.FC<BlogListProps> = ({
  posts,
  viewMode,
  searchTerm,
  tabFilter,
  isLoading,
  isDeleting,
  onEditPost,
  onViewPost,
  onSetViewMode,
  onSearchChange,
  onFilterChange,
  onLoadPosts
}) => {
  // Filter posts based on search term and tab filter
  const filteredPosts = posts.filter(post => {
    // Apply search filter
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
                         (post.slug && post.slug.toLowerCase().includes(searchTerm.toLowerCase())) ||
                         (post.tags && post.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase())));
    
    // Apply tab filter
    const isPublished = post.published ?? post.status === "published";
    const matchesTab = tabFilter === "all" || 
                      (tabFilter === "published" && isPublished) ||
                      (tabFilter === "drafts" && !isPublished);
    
    return matchesSearch && matchesTab;
  });

  // Handle post deletion
  const handleDeletePost = async (id: string) => {
    if (isBrowser && globalThis.confirm("Tem certeza que deseja excluir este post? Esta ação não pode ser desfeita.")) {
      try {
        const { error } = await supabase
          .from('blog_posts')
          .delete()
          .eq('id', id);
          
        if (error) {
          throw error;
        }
        
        onLoadPosts();
        toast.success("Post excluído com sucesso");
      } catch (error) {
        console.error("Error deleting post:", error);
        toast.error("Erro ao excluir o post");
      }
    }
  };

  // Helper function to render author information
  const renderAuthor = (author: any) => {
    if (typeof author === 'string') {
      return author;
    } else if (author && typeof author === 'object') {
      return author.name || 'Unknown Author';
    }
    return 'Unknown Author';
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center p-12">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-toctoc-blue"></div>
        <span className="ml-3">Carregando posts...</span>
      </div>
    );
  }

  if (filteredPosts.length === 0) {
    return (
      <div className="bg-white p-12 rounded-lg shadow text-center">
        <p className="text-gray-500">Nenhum post encontrado</p>
      </div>
    );
  }

  if (viewMode === "list") {
    return (
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="divide-y">
          {filteredPosts.map(post => (
            <div key={post.id} className="p-4 hover:bg-gray-50 transition-colors">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <Checkbox id={`post-${post.id}`} />
                  <div className="w-16 h-16 rounded bg-gray-100 overflow-hidden hidden md:block">
                    <img 
                      src={post.featured_image || post.cover_url || ""} 
                      alt={post.title}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = "https://via.placeholder.com/120?text=Sem+Imagem";
                      }} 
                    />
                  </div>
                  <div>
                    <h3 className="font-medium flex items-center">
                      {post.title}
                      {!(post.published ?? post.status === "published") && (
                        <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-600">
                          Rascunho
                        </span>
                      )}
                    </h3>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {post.tags && post.tags.slice(0, 3).map((tag, index) => (
                        <span key={index} className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                          #{tag}
                        </span>
                      ))}
                      {post.tags && post.tags.length > 3 && (
                        <span className="text-xs text-gray-500">
                          +{post.tags.length - 3}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-500 mt-1">
                      <span>
                        {new Date(post.created_at || "").toLocaleDateString('pt-BR')}
                      </span>
                      <span>•</span>
                      <span>{renderAuthor(post.author)}</span>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <Button 
                    size="sm" 
                    variant="ghost"
                    onClick={() => onViewPost(post)}
                  >
                    <Eye className="w-4 h-4" />
                  </Button>
                  <Button 
                    size="sm" 
                    variant="ghost"
                    onClick={() => onEditPost(post.id as string)}
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button 
                    size="sm" 
                    variant="ghost" 
                    className="text-red-500 hover:text-red-700"
                    onClick={() => handleDeletePost(post.id as string)}
                    disabled={isDeleting === post.id}
                  >
                    {isDeleting === post.id ? (
                      <Loader className="w-4 h-4 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  } else {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredPosts.map(post => (
          <Card key={post.id}>
            <div className="relative h-40 bg-gray-100">
              <img 
                src={post.featured_image || post.cover_url || ""} 
                alt={post.title}
                className="w-full h-full object-cover"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = "https://via.placeholder.com/400x200?text=Sem+Imagem";
                }} 
              />
                      {!(post.published ?? post.status === "published") && (
                <div className="absolute top-2 right-2 px-2 py-1 text-xs rounded bg-white text-gray-600">
                  Rascunho
                </div>
              )}
            </div>
            <CardContent className="p-4">
              <h3 className="font-medium line-clamp-1">{post.title}</h3>
              <div className="flex flex-wrap gap-1 my-2">
                {post.tags && post.tags.slice(0, 2).map((tag, index) => (
                  <span key={index} className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                    #{tag}
                  </span>
                ))}
                {post.tags && post.tags.length > 2 && (
                  <span className="text-xs text-gray-500">
                    +{post.tags.length - 2}
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-500 mb-2">
                {new Date(post.created_at || "").toLocaleDateString('pt-BR')} • {post.category || "Sem categoria"}
              </p>
              <p className="text-sm text-gray-600 line-clamp-2">
                {post.excerpt || ""}
              </p>
              <div className="mt-4 flex justify-between">
                <Button 
                  size="sm" 
                  variant="ghost"
                  onClick={() => onEditPost(post.id as string)}
                >
                  <Edit className="w-4 h-4 mr-1" /> Editar
                </Button>
                <Button 
                  size="sm" 
                  variant="ghost" 
                  className="text-red-500 hover:text-red-700"
                  onClick={() => handleDeletePost(post.id as string)}
                  disabled={isDeleting === post.id}
                >
                  {isDeleting === post.id ? (
                    <Loader className="w-4 h-4 animate-spin mr-1" />
                  ) : (
                    <Trash2 className="w-4 h-4 mr-1" />
                  )}
                  Excluir
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }
};

export default BlogList;
